/**
 * Admin Service Hooks
 * React hooks for tenant and user management operations
 * Requirements: 10.1, 10.2, 10.3, 11.1, 11.2, 11.3, 11.4
 */

import { useState, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import {
  Tenant,
  TenantConnection,
  User,
  UserConnection,
  CreateTenantInput,
  UpdateTenantInput,
  CreateUserInput,
  UpdateUserInput,
  AssignRoleInput,
  UserRole,
  UserStatus,
  TenantStatus,
} from '../types/admin';
import {
  GET_TENANT,
  LIST_TENANTS,
  CREATE_TENANT,
  UPDATE_TENANT,
  DEACTIVATE_TENANT,
  GET_USER,
  LIST_USERS,
  CREATE_USER,
  UPDATE_USER,
  DELETE_USER,
  ASSIGN_ROLE,
  DEACTIVATE_USER,
  PLATFORM_ADMIN_LIST_USERS,
  PLATFORM_ADMIN_CREATE_USER,
  PLATFORM_ADMIN_UPDATE_USER,
  PLATFORM_ADMIN_DELETE_USER,
} from '../graphql/admin';

// Check if we're in preview mode
const isPreviewMode = () =>
  import.meta.env.VITE_PREVIEW === 'true' || !import.meta.env.VITE_COGNITO_USER_POOL_ID;

// ============================================
// Tenant Hooks
// ============================================

/**
 * Hook for fetching a single tenant by ID
 * Requirements: 10.3
 */
export function useGetTenant() {
  const [data, setData] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTenant = useCallback(async (tenantId: string) => {
    try {
      setLoading(true);
      setError(null);

      if (isPreviewMode()) {
        setData({
          tenantId,
          name: 'Preview Tenant',
          status: TenantStatus.ACTIVE,
          userCount: 5,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        return;
      }

      const client = generateClient();
      const response = (await client.graphql({
        query: GET_TENANT,
        variables: { tenantId },
      })) as any;

      setData(response.data?.getTenant || null);
    } catch (err: any) {
      console.error('Failed to fetch tenant:', err);
      setError(err.message || 'Failed to fetch tenant');
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchTenant, refetch: fetchTenant };
}

/**
 * Hook for listing tenants with pagination
 * Requirements: 10.1
 */
export function useTenants(pageSize: number = 10) {
  const [data, setData] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | null>(null);

  const fetchTenants = useCallback(
    async (token?: string | null) => {
      try {
        setLoading(true);
        setError(null);

        if (isPreviewMode()) {
          setData([
            {
              tenantId: '1',
              name: 'Acme Corp',
              status: TenantStatus.ACTIVE,
              userCount: 15,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              tenantId: '2',
              name: 'Beta Inc',
              status: TenantStatus.ACTIVE,
              userCount: 8,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          ]);
          setNextToken(null);
          return;
        }

        const client = generateClient();
        const response = (await client.graphql({
          query: LIST_TENANTS,
          variables: { limit: pageSize, nextToken: token },
        })) as any;

        const connection: TenantConnection = response.data?.listTenants;
        if (connection) {
          setData(connection.items);
          setNextToken(connection.nextToken);
        }
      } catch (err: any) {
        console.error('Failed to fetch tenants:', err);
        setError(err.message || 'Failed to fetch tenants');
      } finally {
        setLoading(false);
      }
    },
    [pageSize]
  );

  return { data, loading, error, nextToken, fetchTenants, refetch: () => fetchTenants() };
}


/**
 * Hook for creating a new tenant
 * Requirements: 10.2
 */
export function useCreateTenant() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createTenant = useCallback(async (input: CreateTenantInput): Promise<Tenant | null> => {
    try {
      setLoading(true);
      setError(null);

      if (isPreviewMode()) {
        return {
          tenantId: Date.now().toString(),
          name: input.name,
          status: TenantStatus.ACTIVE,
          userCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      }

      const client = generateClient();
      const response = (await client.graphql({
        query: CREATE_TENANT,
        variables: { input },
      })) as any;

      return response.data?.createTenant || null;
    } catch (err: any) {
      console.error('Failed to create tenant:', err);
      setError(err.message || 'Failed to create tenant');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createTenant, loading, error };
}

/**
 * Hook for updating an existing tenant
 * Requirements: 10.3
 */
export function useUpdateTenant() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateTenant = useCallback(
    async (tenantId: string, input: UpdateTenantInput): Promise<Tenant | null> => {
      try {
        setLoading(true);
        setError(null);

        if (isPreviewMode()) {
          return {
            tenantId,
            name: input.name || 'Updated Tenant',
            status: input.status || TenantStatus.ACTIVE,
            userCount: 5,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
        }

        const client = generateClient();
        const response = (await client.graphql({
          query: UPDATE_TENANT,
          variables: { tenantId, input },
        })) as any;

        return response.data?.updateTenant || null;
      } catch (err: any) {
        console.error('Failed to update tenant:', err);
        setError(err.message || 'Failed to update tenant');
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { updateTenant, loading, error };
}

/**
 * Hook for deactivating a tenant
 * Requirements: 10.3
 */
export function useDeactivateTenant() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deactivateTenant = useCallback(async (tenantId: string): Promise<Tenant | null> => {
    try {
      setLoading(true);
      setError(null);

      if (isPreviewMode()) {
        return {
          tenantId,
          name: 'Deactivated Tenant',
          status: TenantStatus.INACTIVE,
          userCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      }

      const client = generateClient();
      const response = (await client.graphql({
        query: DEACTIVATE_TENANT,
        variables: { tenantId },
      })) as any;

      return response.data?.deactivateTenant || null;
    } catch (err: any) {
      console.error('Failed to deactivate tenant:', err);
      setError(err.message || 'Failed to deactivate tenant');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deactivateTenant, loading, error };
}


// ============================================
// User Hooks
// ============================================

/**
 * Hook for fetching a single user by ID
 * Requirements: 11.3
 */
export function useGetUser() {
  const [data, setData] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = useCallback(async (userId: string) => {
    try {
      setLoading(true);
      setError(null);

      if (isPreviewMode()) {
        setData({
          userId,
          tenantId: 'tenant-1',
          role: UserRole.USER,
          cognitoSub: 'sub-preview',
          email: 'preview@example.com',
          name: 'Preview User',
          status: UserStatus.ACTIVE,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        return;
      }

      const client = generateClient();
      const response = (await client.graphql({
        query: GET_USER,
        variables: { userId },
      })) as any;

      setData(response.data?.getUser || null);
    } catch (err: any) {
      console.error('Failed to fetch user:', err);
      setError(err.message || 'Failed to fetch user');
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchUser, refetch: fetchUser };
}

/**
 * Hook for listing users with pagination
 * Requirements: 11.1
 */
export function useUsers(tenantId: string | null, pageSize: number = 10) {
  const [data, setData] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | null>(null);

  const fetchUsers = useCallback(
    async (token?: string | null) => {
      if (!tenantId) return;

      try {
        setLoading(true);
        setError(null);

        if (isPreviewMode()) {
          setData([
            {
              userId: '1',
              tenantId: 'tenant-1',
              role: UserRole.ADMIN,
              cognitoSub: 'sub-1',
              email: 'admin@example.com',
              name: 'John Admin',
              status: UserStatus.ACTIVE,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              userId: '2',
              tenantId: 'tenant-1',
              role: UserRole.USER,
              cognitoSub: 'sub-2',
              email: 'user@example.com',
              name: 'Jane User',
              status: UserStatus.ACTIVE,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          ]);
          setNextToken(null);
          return;
        }

        const client = generateClient();
        const response = (await client.graphql({
          query: LIST_USERS,
          variables: { tenantId, limit: pageSize, nextToken: token },
        })) as any;

        const connection: UserConnection = response.data?.listUsers;
        if (connection) {
          setData(connection.items);
          setNextToken(connection.nextToken);
        }
      } catch (err: any) {
        console.error('Failed to fetch users:', err);
        setError(err.message || 'Failed to fetch users');
      } finally {
        setLoading(false);
      }
    },
    [tenantId, pageSize]
  );

  return { data, loading, error, nextToken, fetchUsers, refetch: () => fetchUsers() };
}


/**
 * Hook for creating a new user
 * Requirements: 11.2
 */
export function useCreateUser() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createUser = useCallback(async (input: CreateUserInput): Promise<User | null> => {
    try {
      setLoading(true);
      setError(null);

      if (isPreviewMode()) {
        return {
          userId: Date.now().toString(),
          tenantId: 'tenant-1',
          role: input.role || UserRole.USER,
          cognitoSub: null,
          email: input.email,
          name: input.name,
          status: UserStatus.PENDING_SYNC,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      }

      const client = generateClient();
      const response = (await client.graphql({
        query: CREATE_USER,
        variables: { input },
      })) as any;

      return response.data?.createUser || null;
    } catch (err: any) {
      console.error('Failed to create user:', err);
      setError(err.message || 'Failed to create user');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createUser, loading, error };
}

/**
 * Hook for updating an existing user
 * Requirements: 11.3
 */
export function useUpdateUser() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateUser = useCallback(
    async (userId: string, input: UpdateUserInput): Promise<User | null> => {
      try {
        setLoading(true);
        setError(null);

        if (isPreviewMode()) {
          return {
            userId,
            tenantId: 'tenant-1',
            role: UserRole.USER,
            cognitoSub: 'sub-preview',
            email: input.email || 'updated@example.com',
            name: input.name || 'Updated User',
            status: input.status || UserStatus.ACTIVE,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
        }

        const client = generateClient();
        const response = (await client.graphql({
          query: UPDATE_USER,
          variables: { userId, input },
        })) as any;

        return response.data?.updateUser || null;
      } catch (err: any) {
        console.error('Failed to update user:', err);
        setError(err.message || 'Failed to update user');
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { updateUser, loading, error };
}

/**
 * Hook for deleting a user
 * Requirements: 11.3
 */
export function useDeleteUser() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteUser = useCallback(
    async (userId: string): Promise<{ userId: string; message: string } | null> => {
      try {
        setLoading(true);
        setError(null);

        if (isPreviewMode()) {
          return {
            userId,
            message: 'User deleted successfully',
          };
        }

        const client = generateClient();
        const response = (await client.graphql({
          query: DELETE_USER,
          variables: { userId },
        })) as any;

        return response.data?.deleteUser || null;
      } catch (err: any) {
        console.error('Failed to delete user:', err);
        setError(err.message || 'Failed to delete user');
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { deleteUser, loading, error };
}


/**
 * Hook for assigning a role to a user
 * Requirements: 11.4
 */
export function useAssignRole() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assignRole = useCallback(async (input: AssignRoleInput): Promise<User | null> => {
    try {
      setLoading(true);
      setError(null);

      if (isPreviewMode()) {
        return {
          userId: input.userId,
          tenantId: 'tenant-1',
          role: input.role,
          cognitoSub: 'sub-preview',
          email: 'user@example.com',
          name: 'User',
          status: UserStatus.ACTIVE,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      }

      const client = generateClient();
      const response = (await client.graphql({
        query: ASSIGN_ROLE,
        variables: { input },
      })) as any;

      return response.data?.assignRole || null;
    } catch (err: any) {
      console.error('Failed to assign role:', err);
      setError(err.message || 'Failed to assign role');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { assignRole, loading, error };
}

/**
 * Hook for deactivating a user
 * Requirements: 11.3
 */
export function useDeactivateUser() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deactivateUser = useCallback(async (userId: string): Promise<User | null> => {
    try {
      setLoading(true);
      setError(null);

      if (isPreviewMode()) {
        return {
          userId,
          tenantId: 'tenant-1',
          role: UserRole.USER,
          cognitoSub: 'sub-preview',
          email: 'deactivated@example.com',
          name: 'Deactivated User',
          status: UserStatus.INACTIVE,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      }

      const client = generateClient();
      const response = (await client.graphql({
        query: DEACTIVATE_USER,
        variables: { userId },
      })) as any;

      return response.data?.deactivateUser || null;
    } catch (err: any) {
      console.error('Failed to deactivate user:', err);
      setError(err.message || 'Failed to deactivate user');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deactivateUser, loading, error };
}


// ============================================
// Platform Admin User Hooks
// ============================================

/**
 * Hook for listing users in a tenant as Platform Admin
 * Requirements: 7.4
 */
export function usePlatformAdminUsers(tenantId: string | null, pageSize: number = 10) {
  const [data, setData] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | null>(null);

  const fetchUsers = useCallback(
    async (token?: string | null) => {
      if (!tenantId) return;

      try {
        setLoading(true);
        setError(null);

        if (isPreviewMode()) {
          setData([
            {
              userId: '1',
              tenantId,
              role: UserRole.ADMIN,
              cognitoSub: 'sub-1',
              email: 'admin@example.com',
              name: 'John Admin',
              status: UserStatus.ACTIVE,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              userId: '2',
              tenantId,
              role: UserRole.USER,
              cognitoSub: 'sub-2',
              email: 'user@example.com',
              name: 'Jane User',
              status: UserStatus.ACTIVE,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          ]);
          setNextToken(null);
          return;
        }

        const client = generateClient();
        const response = (await client.graphql({
          query: PLATFORM_ADMIN_LIST_USERS,
          variables: { tenantId, limit: pageSize, nextToken: token },
        })) as any;

        const connection: UserConnection = response.data?.platformAdminListUsers;
        if (connection) {
          setData(connection.items);
          setNextToken(connection.nextToken);
        }
      } catch (err: any) {
        console.error('Failed to fetch platform admin users:', err);
        setError(err.message || 'Failed to fetch users');
      } finally {
        setLoading(false);
      }
    },
    [tenantId, pageSize]
  );

  return { data, loading, error, nextToken, fetchUsers, refetch: () => fetchUsers() };
}

/**
 * Hook for creating a user in a tenant as Platform Admin
 * Requirements: 7.4
 */
export function usePlatformAdminCreateUser() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createUser = useCallback(
    async (tenantId: string, input: CreateUserInput): Promise<User | null> => {
      try {
        setLoading(true);
        setError(null);

        if (isPreviewMode()) {
          return {
            userId: Date.now().toString(),
            tenantId,
            role: input.role || UserRole.USER,
            cognitoSub: null,
            email: input.email,
            name: input.name,
            status: UserStatus.PENDING_SYNC,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
        }

        const client = generateClient();
        const response = (await client.graphql({
          query: PLATFORM_ADMIN_CREATE_USER,
          variables: { tenantId, input },
        })) as any;

        return response.data?.platformAdminCreateUser || null;
      } catch (err: any) {
        console.error('Failed to create platform admin user:', err);
        setError(err.message || 'Failed to create user');
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { createUser, loading, error };
}

/**
 * Hook for updating a user in a tenant as Platform Admin
 * Requirements: 7.4
 */
export function usePlatformAdminUpdateUser() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateUser = useCallback(
    async (tenantId: string, userId: string, input: UpdateUserInput): Promise<User | null> => {
      try {
        setLoading(true);
        setError(null);

        if (isPreviewMode()) {
          return {
            userId,
            tenantId,
            role: UserRole.USER,
            cognitoSub: 'sub-preview',
            email: input.email || 'updated@example.com',
            name: input.name || 'Updated User',
            status: input.status || UserStatus.ACTIVE,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
        }

        const client = generateClient();
        const response = (await client.graphql({
          query: PLATFORM_ADMIN_UPDATE_USER,
          variables: { tenantId, userId, input },
        })) as any;

        return response.data?.platformAdminUpdateUser || null;
      } catch (err: any) {
        console.error('Failed to update platform admin user:', err);
        setError(err.message || 'Failed to update user');
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { updateUser, loading, error };
}

export function usePlatformAdminDeleteUser() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteUser = useCallback(async (tenantId: string, userId: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      if (isPreviewMode()) {
        return true;
      }

      const client = generateClient();
      await client.graphql({
        query: PLATFORM_ADMIN_DELETE_USER,
        variables: { tenantId, userId },
      });

      return true;
    } catch (err: any) {
      console.error('Failed to delete platform admin user:', err);
      setError(err.message || 'Failed to delete user');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteUser, loading, error };
}
